/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { ComponentInterface } from '@appCanvasCommon/interfaces/component.interface';
import { TicketContainer } from '@appPlanApplication/tickets/TicketContainer';
import { Component } from '@appCanvasCommon/component-system/Component';
import { fbReporting } from 'src/app/plain-javascript/background-scripts/fb-reporting';
import { Interaction } from 'src/app/plain-javascript/services/interaction-blocker';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { Ticket } from '@touchplan/common';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';
import { CursorType } from '@appCanvasCommon/events/cursor-type.enum';
import { filter, Subject, take, takeUntil } from 'rxjs';
import { TicketList } from 'src/app/common/fancy-firebase-ticket/lists/TicketList';
import * as strings from '@touchplan/strings';
import { DynamicFlagTypes } from '@touchplan/feature-flags';

export class TicketOpenEdit extends Component implements ComponentInterface {
	private _ticketContainer: TicketContainer;
	private shutdownSubject$ = new Subject<void>();

	constructor() {
		super();
	}
	/**
	 * The setup method is called once all properties on a component have been decorated by the ComponentManager.
	 *
	 * From this point on, you can safely use this.getParent() and this.getManager().
	 *
	 * Arguments passed into setup are passed in via the ComponentManager.add(name, component, ...args)
	 */
	public override setup(canvasAngularLand: CanvasAngularLand, dataContainer: TicketContainer): void {
		this.canvasAngularLand = canvasAngularLand;
		this._ticketContainer = dataContainer;

		const parent = this.getParent();
		parent.eventMode = EventMode.Static;
		parent.cursor = CursorType.Pointer;

		const singleTap = () => {
			dataContainer.cutInLine();
			//planState.actions.changeTicketViewToEdit(dataContainer.getData());
			const tappedTicket = dataContainer.getData();
			if (Zone.current.name !== 'angular') {
				fbReporting.reportRealWarning('Zone is outside angular zone, things will be slow');
			}

			const selectedTicketList = canvasAngularLand.floatingTickets.editTicket$.getValue().size
				? canvasAngularLand.floatingTickets
				: canvasAngularLand.tickets;

			// cycle tickets
			if (
				canvasAngularLand.interactionBlocker.check(Interaction.OpenEditTicket) && // interaction blocker
				selectedTicketList.selectedTicket$.getValue().list.size > 1 && // make sure there is more than one ticket selected
				this.checkSelectedTicketsContainTappedTicket(
					selectedTicketList.selectedTicket$.getValue().list,
					tappedTicket.$id
				)
			) {
				// function name explains it :)
				const editTickets = selectedTicketList.editTicket$.getValue();
				canvasAngularLand.ticketEditCommonService.trySave();
				canvasAngularLand.actions.persistTicketData(editTickets, selectedTicketList);
				canvasAngularLand.actions.changeTicketViewToDefault(editTickets, selectedTicketList);
				canvasAngularLand.actions.unselectCycleTicket(
					canvasAngularLand.tickets.selectedTicket$.getValue().list
				);
				canvasAngularLand.actions.selectCycleTicket(tappedTicket);
				canvasAngularLand.actions.changeTicketViewToEdit(tappedTicket);
				canvasAngularLand.cycleTicket$.next(tappedTicket);
			} else if (canvasAngularLand.interactionBlocker.check(Interaction.OpenEditTicket)) {
				canvasAngularLand.actions.clearSelection(canvasAngularLand.tickets.selectedTicket$.getValue().list);
				canvasAngularLand.actions.selectTicket(tappedTicket);
				canvasAngularLand.legacyActions.clearFloatingTickets();
				canvasAngularLand.actions.changeTicketViewToEdit(tappedTicket);

				//never floating from here
				analyticsEventsService.viewedATicket(
					tappedTicket.$id,
					canvasAngularLand.planId,
					canvasAngularLand.planAccessCheck.hasRestrictedAccessSync,
					false
				);
			} else if (selectedTicketList.editTicket$.getValue().size) {
				const editTickets = selectedTicketList.editTicket$.getValue();
				// MOC-4035 - Swap tapped ticket to edit
				if (
					!this.canvasAngularLand.ticketEditCommonService.isTicketEditOpenSync() && // only use the interaction blocker if docked ticket is not open
					(!canvasAngularLand.interactionBlocker.check(Interaction.SwitchEditTicket) ||
						editTickets.get(tappedTicket.$id))
				) {
					return;
				}

				if (
					(this.canvasAngularLand.ticketEditCommonService.isTicketEditOpenSync() &&
						!this.canvasAngularLand.ticketEditCommonService.isEditValidSync()) ||
					editTickets.get(tappedTicket.$id)
				) {
					Logging.warning(strings.TICKET_ERRORS_EDIT_INVALID_SWITCH_TICKET);
					return;
				}

				if (
					!this.canvasAngularLand.featureFlagManager.getFlag(
						DynamicFlagTypes.DOCKED_TICKET_IN_DESKTOP_ENABLED
					)
				) {
					this.saveAndRefreshTicketView(editTickets, selectedTicketList);
					this.selectEditAndFocusTicket(tappedTicket, dataContainer);
				} else {
					if (this.canvasAngularLand.ticketEditCommonService.isTicketEditOpenSync()) {
						// If the UI is open, we need to wait for it to close before selecting the ticket
						canvasAngularLand.ticketEditCommonService
							.isTicketEditOpen()
							.pipe(
								filter((isOpen) => !isOpen), // Only act when closing is complete
								take(1), // Take the first emission and complete
								takeUntil(this.shutdownSubject$)
							)
							.subscribe(() => {
								this.selectEditAndFocusTicket(tappedTicket, dataContainer);
							});
						this.saveAndRefreshTicketView(editTickets, selectedTicketList);
					} else {
						// If the UI is not open, we can select and focus the ticket immediately
						this.saveAndRefreshTicketView(editTickets, selectedTicketList);
						this.selectEditAndFocusTicket(tappedTicket, dataContainer);
					}
				}
			}

			canvasAngularLand.hoverOverStateService.endHover();
		};

		parent.on('hammer-singletap', singleTap);
	}

	private saveAndRefreshTicketView(editTickets: Map<string, Ticket>, selectedTicketList: TicketList) {
		this.canvasAngularLand.ticketEditCommonService.trySave();
		this.canvasAngularLand.actions.clearSelection(
			this.canvasAngularLand.tickets.selectedTicket$.getValue().list
		);
		this.canvasAngularLand.actions.changeTicketViewToDefault(editTickets, selectedTicketList);
		this.canvasAngularLand.actions.unSelectTicket(
			selectedTicketList.selectedTicket$.getValue().list,
			selectedTicketList
		);
	}

	private selectEditAndFocusTicket(tappedTicket: any, dataContainer: TicketContainer): void {
		this.canvasAngularLand.actions.selectTicket(tappedTicket);
		//open new one
		this.canvasAngularLand.actions.changeTicketViewToEdit(tappedTicket);
		// Focus on new ticket
		dataContainer.camera.focusOnTicket(tappedTicket, { z: 0.5 });
	}

	private checkSelectedTicketsContainTappedTicket(tickets: Map<string, Ticket>, id: string): boolean {
		return Array.from(tickets.values()).find((ticket) => ticket.$id === id) !== undefined;
	}

	/**
	 * Destroy shit or else
	 */
	public override destroy() {
		const parent = this.getParent();
		parent.off('hammer-singletap');
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
		super.destroy();
	}
}
